import React, { useContext } from "react";
import {
  ListItem,
  List,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Box,
  Divider,
  Link as MuiLink,
  Breadcrumbs,
  Skeleton,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import * as queries from "../../../operations/queries";
import { CountryContext } from "../MainComponent";
import { useParams, Link } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

const BlogDetail = () => {
  const { store } = useContext(CountryContext);
  const { blogDetail } = useParams();
  const urlKey = blogDetail?.replace(".html", "");
   const {t} = useTranslation()

  const { data: filterDataRes, loading } = useQuery(
    queries.productQueries.BLOG_DETAIL,
    {
      context: {
        headers: {
          Connection: "keep-alive",
          // store: store,
        },
      },
      variables: {
        url_key: urlKey,
      },
    }
  );

  const blogData = filterDataRes?.getSingleBlog;
  const recentPosts = blogData?.recentPosts;
  const blogDescription = { __html: blogData?.description };

  if (loading) {
    return (
      <>
        <Box sx={{ py: 4, px: 4 }} className="directionrtl">
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs
              separator={<ArrowForwardIosIcon fontSize="small" />}
              aria-label="breadcrumb"
              className="breadcrumb-data"
            >
              <Skeleton width="90px" height={20} />
              <Skeleton width="90px" height={20} />
              <Skeleton width="200px" height={20} />
            </Breadcrumbs>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 4 }}>
                <Skeleton
                  variant="rectangular"
                  height={400}
                  sx={{
                    width: "100%",
                    bgcolor: "grey.300",
                  }}
                />
                <CardContent>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={48}
                    sx={{
                      mt: 2,
                      bgcolor: "grey.300",
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="30%"
                    height={24}
                    sx={{
                      bgcolor: "grey.300",
                    }}
                  />
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    {[...Array(7)].map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="text"
                        width={index % 2 ? "90%" : "100%"}
                        height={24}
                        sx={{
                          mb: 2,
                          bgcolor: "grey.300",
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={36}
                    sx={{
                      mb: 2,
                      bgcolor: "grey.300",
                    }}
                  />

                  <Box>
                    {[...Array(5)].map((_, index) => (
                      <React.Fragment key={index}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            py: 1.5,
                          }}
                        >
                          <Skeleton
                            variant="circular"
                            width={16}
                            height={16}
                            sx={{
                              mr: 1,
                              bgcolor: "grey.300",
                            }}
                          />
                          <Skeleton
                            variant="text"
                            width="70%"
                            height={24}
                            sx={{
                              bgcolor: "grey.300",
                            }}
                          />
                        </Box>
                        {index < 4 && <Divider />}
                      </React.Fragment>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{blogData?.meta_title}</title>
        <meta name="description" content={blogData?.meta_description} />
        <meta name="urlKey" content={blogData?.meta_url_key} />
        <meta name="DC.title" content={blogData?.meta_title} />
      </Helmet>
      <Box sx={{ py: 4, px: 4 }} className="directionrtl">
        <Breadcrumbs
          separator={<ArrowForwardIosIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <MuiLink component={Link} to={`/${store}`} color="inherit">
          {t("blog.home")}
          </MuiLink>
          <MuiLink component={Link} to={`/${store}/blog.html`} color="inherit">
          {t("blog.blogTitle")}
          </MuiLink>
          <Typography color="text.primary">{blogData?.title}</Typography>
        </Breadcrumbs>

        <Grid container spacing={2}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 4 }}>
              {blogData?.banner && (
                <CardMedia
                  component="img"
                  image={blogData.banner}
                  alt={blogData.title}
                  sx={{ objectFit: "cover" }}
                />
              )}

              <CardContent>
                <Typography
                  gutterBottom
                  sx={{ fontWeight: 600, mt: 2 }}
                  className="blog-title"
                >
                  {blogData?.title}
                </Typography>

                {blogData?.created_at && (
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {/* {format(new Date(blogData.created_at), 'MMMM dd, yyyy')} */}
                  </Typography>
                )}

                <Divider sx={{ my: 3 }} />

                <Box
                  component="div"
                  dangerouslySetInnerHTML={blogDescription}
                  sx={{
                    "& p": { mb: 3, lineHeight: 1.8 },
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: 1,
                      my: 2,
                    },
                    "& h1": {
                      fontSize: "1.8rem",
                      fontWeight: 600,
                      mt: 4,
                      mb: 2,
                    },
                    "& h3": {
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      mt: 3,
                      mb: 2,
                    },
                    "& ul, & ol": { pl: 4, mb: 3 },
                    "& li": { mb: 1 },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    position: "relative",
                  }}
                >
                  {t("blog.recent_post")}
                </Typography>
                <hr style={{paddingBottom: 1,backgroundColor: "#10CDF2", height: "3px",}}/>

                <List disablePadding>
                  {recentPosts?.map((post: any, index: number) => (
                    <React.Fragment key={index}>
                      <ListItem
                        disableGutters
                        sx={{
                          py: 1.5,
                          alignItems: "flex-start",
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <MuiLink
                          component={Link}
                          to={`/${store}/blog/${post.link}`}
                          color="text.primary"
                          underline="hover"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <ArrowForwardIosIcon
                            color="primary"
                            sx={{ fontSize: "0.8rem", mr: 1 ,ml:`${store.includes("ar") ? "8px !important" : 0}` }}
                          />
                          {post.title}
                        </MuiLink>
                      </ListItem>
                      {index < recentPosts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default BlogDetail;
